import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { BreadcrumbItem } from './subcomponents/BreadcrumbItem';
import { BreadcrumbMenu } from './subcomponents/BreadcrumbMenu';
import { BreadcrumbLink } from './types';
import './Breadcrumb.css';

interface Props {
  links: BreadcrumbLink[];
  /** Accessible label for a crumb's loading placeholder (localized by the consumer). */
  loadingLabel?: string;
}

// Decorative chevron between crumbs. It's a sibling of the crumbs (not nested
// inside them) so the row's `gap` spaces everything evenly and `align-center`
// vertically centers it against the labels. Hidden from assistive tech.
function BreadcrumbSeparator() {
  return (
    <span aria-hidden className="eds-breadcrumb-separator">
      <Icon icon="chevron-right" size="s" />
    </span>
  );
}

export const Breadcrumb = memo(({ links, loadingLabel }: Props) => {
  const navRef = useRef<HTMLElement>(null);
  // Collapsing is driven purely by fit: a trail that doesn't fit its container
  // gives up crumbs instead of wrapping to a second row or clipping a label.
  // However many crumbs there are, they all stay visible while they fit — the
  // "…" appears only once one has to go.
  const [widthCollapsedCount, setWidthCollapsedCount] = useState(0);

  // Only the current page is unconditional. Every ancestor can end up in the
  // menu rather than be clipped: a crumb you can't read is worse than one behind
  // the "…", which at least says more exists and opens to show it.
  const maxCollapsedCount = Math.max(links.length - 1, 0);
  const collapsedCount = Math.min(widthCollapsedCount, maxCollapsedCount);

  // The first crumb (the root of the trail) is the last ancestor to go, so it
  // stays put until everything between it and the current page has collapsed.
  const leadingCount =
    links.length > 1 && collapsedCount < links.length - 1 ? 1 : 0;
  const collapsedLinks = links.slice(
    leadingCount,
    leadingCount + collapsedCount,
  );
  const trailingLinks = links.slice(leadingCount + collapsedCount);

  // Identifies the trail by its labels, not by the array identity: consumers
  // rebuild `links` on every render.
  const trailSignature = links.map((link) => link.text ?? '').join(' ');

  // `measure` is called from a ResizeObserver, which fires outside render, so
  // the current collapse state reaches it through a ref.
  const collapseStateRef = useRef({ collapsedCount, maxCollapsedCount });
  collapseStateRef.current = { collapsedCount, maxCollapsedCount };

  // Labels can ellipsize (see BreadcrumbItem.css), but that state is only ever a
  // measurement: a clipped label means the trail has outgrown its container, so
  // collapse one more crumb into the menu. Each collapse re-measures until
  // nothing is clipped or only the current page is left.
  const measure = useCallback(() => {
    const nav = navRef.current;
    if (
      !nav ||
      collapseStateRef.current.collapsedCount >=
        collapseStateRef.current.maxCollapsedCount
    ) {
      return;
    }

    const isAnyLabelClipped = Array.from(
      nav.querySelectorAll<HTMLElement>('.eds-breadcrumb-item > *'),
      // Sub-pixel label widths round up, so require a whole pixel of overflow.
    ).some((label) => label.scrollWidth > label.clientWidth + 1);

    if (isAnyLabelClipped) {
      setWidthCollapsedCount((count) => count + 1);
    }
  }, []);

  // Re-measure after every render that changed what's on screen — a collapse, a
  // reset, or a new trail. Before paint, so the collapsed result is what the
  // user sees rather than the clipped one.
  useLayoutEffect(measure, [
    measure,
    collapsedCount,
    maxCollapsedCount,
    trailSignature,
  ]);

  // Resizing the container doesn't re-render this component, so the observer has
  // to drive the re-measure itself: narrower means collapse further, wider means
  // start over from the full trail and let the effect above re-collapse.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') {
      return;
    }

    let lastWidth = nav.getBoundingClientRect().width;
    const observer = new ResizeObserver(([entry]) => {
      const { width } = entry.contentRect;
      if (width === lastWidth) {
        return;
      }
      const hasGrown = width > lastWidth;
      lastWidth = width;

      if (hasGrown) {
        setWidthCollapsedCount(0);
        return;
      }
      measure();
    });
    observer.observe(nav);

    return () => observer.disconnect();
  }, [measure]);

  // A new trail starts fresh: the previous route's collapse count says nothing
  // about how wide this one is.
  useEffect(() => {
    setWidthCollapsedCount(0);
  }, [trailSignature]);

  const hasLeadingCrumbs = leadingCount > 0 || collapsedLinks.length > 0;

  return (
    <Layout
      align="center"
      as="nav"
      className="eds-breadcrumb"
      ref={navRef}
      spacing="s"
    >
      {leadingCount > 0 && (
        <BreadcrumbItem
          isLast={links.length === 1}
          link={links[0]}
          loadingLabel={loadingLabel}
        />
      )}
      {collapsedLinks.length > 0 && (
        <>
          {leadingCount > 0 && <BreadcrumbSeparator />}
          <BreadcrumbMenu links={collapsedLinks} />
        </>
      )}
      {trailingLinks.map((link, index) => (
        <Fragment key={`${link.path}-${index}`}>
          {(index > 0 || hasLeadingCrumbs) && <BreadcrumbSeparator />}
          <BreadcrumbItem
            isLast={index === trailingLinks.length - 1}
            link={link}
            loadingLabel={loadingLabel}
          />
        </Fragment>
      ))}
    </Layout>
  );
});

Breadcrumb.displayName = 'Breadcrumb';
